import { PHASE_TYPE_OPTIONS, type JobFlowPageProps, type JobPhase } from './JobFlowPage.component';
import type { PhaseStageDraft, SheetSelectOption } from './PhaseSheet';
import type { PhaseDraft } from './PhaseAddDialog';
import { UNLOCKED_RESUBMIT } from './resubmit-lock';

export const emptySheetValues: PhaseStageDraft = {
  name: '',
  type: '',
  startDate: '',
  endDate: '',
  endTime: '',
  canResubmit: '',
};

export const emptyPhaseDraft: PhaseDraft = {
  name: '',
  type: '',
  startDate: '',
  endDate: '',
  // RN-05 do EU-165: horário pré-preenchido com 23:59, editável.
  endTime: '23:59',
};

// EU-165 RN-04 / FAS RN-03 — 4 tipos oficiais oferecidos no picker: Trilha
// online → Jornada de aprofundamento → Dinâmica → Entrevista. Reusa o
// `PHASE_TYPE_OPTIONS` canônico do componente (single source of truth) pra
// evitar drift — `contratacao-final` foi removido do picker (segue válido só
// na leitura de vagas legadas).
export const mockPhaseTypeOptions: SheetSelectOption[] = [...PHASE_TYPE_OPTIONS];

export const mockFormOptions: SheetSelectOption[] = [
  { value: 'f-1', label: 'Formulário de candidatura' },
  { value: 'f-2', label: 'Formulário de avaliação cultural' },
  { value: 'f-3', label: 'Formulário de auto-avaliação' },
];

export const mockTestOptions: SheetSelectOption[] = [
  { value: 't-1', label: 'Raio-X de Carreira' },
  { value: 't-2', label: 'Teste de lógica' },
  { value: 't-3', label: 'Teste de comunicação' },
];

export const mockFatorHOptions: SheetSelectOption[] = [
  { value: 'h-1', label: 'Fator H — Comunicação' },
  { value: 'h-2', label: 'Fator H — Resolução de problemas' },
  { value: 'h-3', label: 'Fator H — Visão estratégica' },
];

export const mockKolabOptions: SheetSelectOption[] = [
  { value: 'ki-1', label: 'Kolab — Fit Cultural' },
  { value: 'ki-2', label: 'Kolab — Perfil Comportamental' },
];

export const mockPhases: JobPhase[] = [
  {
    id: 'phase-1',
    index: 1,
    name: '1ª Fase',
    type: null,
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [
      {
        id: 'stage-1-1',
        name: 'Manual do processo seletivo',
        type: 'conteudo',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: '08:48',
      },
      {
        id: 'stage-1-2',
        name: 'Informações adicionais',
        type: 'form',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: '08:48',
        // RN-06 — etapa com candidatos que já a concluíram: editá-la dispara
        // o modal "Reabrir etapa". RN-10 — `answerCount > 0` também trava o
        // delete (só pode ocultar) e renderiza o informativo no card.
        completedCount: 3,
        answerCount: 3,
      },
    ],
  },
  {
    id: 'phase-2',
    index: 2,
    name: '2ª Fase',
    type: null,
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [
      {
        id: 'stage-2-1',
        name: 'Bate-papo Interativo',
        type: 'fator-h',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: null,
      },
      {
        id: 'stage-2-2',
        name: 'Desafio online',
        type: 'case',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: null,
      },
    ],
  },
  {
    id: 'phase-3',
    index: 3,
    name: '3ª Fase',
    type: null,
    startDate: null,
    endDate: null,
    endTime: '23:59',
    stages: [
      {
        id: 'stage-3-1',
        name: 'Raio-X de Carreira',
        type: 'teste',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: null,
      },
      {
        id: 'stage-3-2',
        name: 'Currículo',
        type: 'curriculo',
        startDate: '2026-05-06',
        endDate: '2026-05-06',
        endTime: null,
      },
    ],
  },
];

const baseProps: Omit<JobFlowPageProps, 'phases'> = {
  jobName: 'Desenvolvimento de softwares',
  onAddPhase: () => undefined,
  onPhaseAction: () => undefined,
  onAddStage: () => undefined,
  onStageAction: () => undefined,
  onBack: () => undefined,
  onReorder: () => undefined,
  onStageDrop: () => undefined,
  isSheetOpen: false,
  onSheetOpenChange: () => undefined,
  sheetValues: emptySheetValues,
  sheetErrors: {},
  sheetIsSubmitting: false,
  sheetFormOptions: mockFormOptions,
  sheetTestOptions: mockTestOptions,
  sheetFatorHOptions: mockFatorHOptions,
  sheetKolabOptions: mockKolabOptions,
  sheetKolabCatalogueState: 'ready',
  onSheetFieldChange: () => undefined,
  onSheetContentFileChange: () => undefined,
  onSheetSubmit: () => undefined,
  isAddPhaseDialogOpen: false,
  onAddPhaseDialogOpenChange: () => undefined,
  addPhaseValues: emptyPhaseDraft,
  addPhaseErrors: {},
  addPhaseIsSubmitting: false,
  addPhaseTypeOptions: mockPhaseTypeOptions,
  onAddPhaseFieldChange: () => undefined,
  onAddPhaseSubmit: () => undefined,
  isDeletePhaseDialogOpen: false,
  onDeletePhaseDialogOpenChange: () => undefined,
  onConfirmDeletePhase: () => undefined,
  isStageConfirmOpen: false,
  onStageConfirmOpenChange: () => undefined,
  stageConfirmAction: null,
  stageConfirmStageName: '',
  onConfirmStageAction: () => undefined,
  isReopenDialogOpen: false,
  onReopenDialogOpenChange: () => undefined,
  onReopenDecision: () => undefined,
  sheetResubmitLock: UNLOCKED_RESUBMIT,
  sheetHasAnswers: false,
};

export const emptyProps: JobFlowPageProps = {
  ...baseProps,
  phases: [],
};

export const filledProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
};

export const sheetOpenProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
  isSheetOpen: true,
};

export const addPhaseDialogOpenProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
  isAddPhaseDialogOpen: true,
};

export const deletePhaseDialogOpenProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
  isDeletePhaseDialogOpen: true,
};

// RN-11 — modal de confirmação de exclusão/ocultação de ETAPA.
export const stageConfirmDialogOpenProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
  isStageConfirmOpen: true,
  stageConfirmAction: 'delete',
  stageConfirmStageName: mockPhases[0].stages[0].name,
};

// RN-07 — modal "Reabrir etapa".
export const reopenDialogOpenProps: JobFlowPageProps = {
  ...baseProps,
  phases: mockPhases,
  isReopenDialogOpen: true,
};
